import { supabase, friendlyError } from "../supabase.js";
import { planMerge, mergedColumns } from "../mergeRows.js";

const BATCH = 400;

/* Creating a tracker from a sheet.
 *
 * The sheet's header row is the column list and its rows are the data. There
 * is no mapping step and no model call, because there is nothing to decide:
 * the workbook already said what the columns are.
 *
 * Replacing an existing tracker retires its rows rather than destroying them,
 * the same as everywhere else, and keeps the tracker itself so its saved views
 * survive. That is the point of the monthly rhythm: the workbook is redrawn,
 * the charts are not.
 */
export async function writeTracker({
  contractId, sheet, fileName, title, existingId = null, userId = null,
  onProgress = () => {},
}) {
  if (!contractId) return { ok: false, error: "No contract is active." };
  if (!sheet?.usable) return { ok: false, error: "That sheet has no table in it." };

  const meta = {
    contract_id: contractId,
    title: (title || sheet.name).trim(),
    source_file: fileName ?? null,
    source_sheet: sheet.name,
    columns: sheet.columns,
    row_count: sheet.rows.length,
    updated_by: userId,
  };

  let trackerId = existingId;
  let retired = 0;

  if (trackerId) {
    onProgress({ phase: "clearing" });
    const cleared = await supabase
      .from("tracker_rows")
      .update({ deleted_at: new Date().toISOString(), updated_by: userId })
      .eq("tracker_id", trackerId)
      .is("deleted_at", null)
      .select("id");
    if (cleared.error) return { ok: false, error: friendlyError(cleared.error) };
    retired = cleared.data?.length ?? 0;

    const upd = await supabase.from("trackers").update(meta).eq("id", trackerId);
    if (upd.error) return { ok: false, error: friendlyError(upd.error) };
  } else {
    const ins = await supabase.from("trackers")
      .insert({ ...meta, added_by: userId }).select("id").single();
    if (ins.error) return { ok: false, error: friendlyError(ins.error) };
    trackerId = ins.data.id;
  }

  let written = 0;
  for (let i = 0; i < sheet.rows.length; i += BATCH) {
    const chunk = sheet.rows.slice(i, i + BATCH).map((data, n) => ({
      tracker_id: trackerId,
      contract_id: contractId,
      row_index: i + n,
      data,
      added_by: userId,
      updated_by: userId,
    }));
    const { error } = await supabase.from("tracker_rows").insert(chunk);
    if (error) {
      return { ok: false, trackerId, written, retired, partial: written > 0,
               error: friendlyError(error) };
    }
    written += chunk.length;
    onProgress({ phase: "writing", written, total: sheet.rows.length });
  }

  return { ok: true, trackerId, written, retired, replaced: !!existingId };
}

/* Filling an existing tracker in from a fresh export.
 *
 * Unlike a replace, this reads what is there first, because the whole point is
 * to leave alone anything the export does not mention. */
export async function mergeTracker({
  contractId, trackerId, sheet, fileName, fromKey, toKey, userId = null,
  onProgress = () => {},
}) {
  if (!contractId || !trackerId) return { ok: false, error: "No tracker to fill in." };
  if (!sheet?.usable) return { ok: false, error: "That sheet has no table in it." };

  const t = await supabase.from("trackers").select("columns").eq("id", trackerId).single();
  if (t.error) return { ok: false, error: friendlyError(t.error) };

  onProgress({ phase: "reading" });
  const existing = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from("tracker_rows").select("id, row_index, data")
      .eq("tracker_id", trackerId).is("deleted_at", null)
      .order("row_index").range(from, from + 999);
    if (error) return { ok: false, error: friendlyError(error) };
    existing.push(...(data ?? []).map((r) => ({ ...r.data, _id: r.id, _index: r.row_index })));
    if (!data || data.length < 1000) break;
  }

  const plan = planMerge({
    existing, incoming: sheet.rows, incomingColumns: sheet.columns, fromKey, toKey,
  });
  if (plan.error) return { ok: false, error: plan.error };

  /* Columns first. A row written with a Latitude the tracker does not list
     would be stored and never drawn. */
  const columns = mergedColumns(t.data.columns ?? [], sheet.columns, fromKey);
  const meta = await supabase.from("trackers")
    .update({ columns, updated_by: userId, source_file: fileName ?? null })
    .eq("id", trackerId);
  if (meta.error) return { ok: false, error: friendlyError(meta.error) };

  let done = 0;
  for (const u of plan.updates) {
    const { error } = await supabase.from("tracker_rows")
      .update({ data: u.data, updated_by: userId }).eq("id", u.id);
    if (error) return { ok: false, error: friendlyError(error), partial: done > 0 };
    done += 1;
    if (done % 25 === 0) onProgress({ phase: "updating", written: done, total: plan.updates.length });
  }

  let nextIndex = existing.reduce((m, r) => Math.max(m, r._index), -1) + 1;
  for (let i = 0; i < plan.inserts.length; i += BATCH) {
    const chunk = plan.inserts.slice(i, i + BATCH).map((data, n) => ({
      tracker_id: trackerId, contract_id: contractId,
      row_index: nextIndex + i + n, data,
      added_by: userId, updated_by: userId,
    }));
    const { error } = await supabase.from("tracker_rows").insert(chunk);
    if (error) return { ok: false, error: friendlyError(error), partial: true };
    onProgress({ phase: "adding", written: i + chunk.length, total: plan.inserts.length });
  }

  const total = existing.length + plan.inserts.length;
  await supabase.from("trackers").update({ row_count: total }).eq("id", trackerId);

  return {
    ok: true, trackerId, merged: true,
    updated: plan.updates.length, added: plan.inserts.length,
    matched: plan.matched, untouched: plan.untouched, blankKeys: plan.blankKeys,
    total,
  };
}
